import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { cx } from '@/lib/cx';
import { formatNumber } from '@/lib/format';
import { Checkbox } from './Checkbox';
import { Loading } from './Loading';

export type SortDirection = 'asc' | 'desc';
export interface SortState {
  key: string;
  direction: SortDirection;
}

export interface Column<T> {
  /** 列キー。accessor 省略時は row[key] を表示・ソートに使う */
  key: string;
  header: ReactNode;
  accessor?: (row: T) => unknown;
  render?: (row: T, index: number) => ReactNode;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  /** 数値列（右寄せ・等幅数字） */
  numeric?: boolean;
  width?: number | string;
  /** 幅を指定しない列の最小幅（px。既定 AUTO_COL_MIN_WIDTH） */
  minWidth?: number;
  /** セルを折り返さない */
  nowrap?: boolean;
}

/** 幅を指定しない列の最小幅。固定幅の列の合計が表示幅を超えても、幅指定の無い列（船名など）が潰れないようにする */
export const AUTO_COL_MIN_WIDTH = 140;

/**
 * 表の最小幅（固定幅の列の合計＋幅指定の無い列の最小幅）。table-layout: fixed では列幅の合計が表の幅を超えると
 * 幅指定の無い列が 0 幅に潰れるため、最小幅を持たせて足りない分は横スクロール（.bs-table-scroll）にする。
 */
function tableMinWidth<T>(columns: ReadonlyArray<Column<T>>, selectable?: boolean): number {
  return columns.reduce(
    (sum, c) => sum + (typeof c.width === 'number' ? c.width : c.width == null ? (c.minWidth ?? AUTO_COL_MIN_WIDTH) : 0),
    selectable ? 44 : 0,
  );
}

export interface PaginationConfig {
  pageSize: number;
  /** サーバーサイドページングの場合に指定（1 始まり） */
  page?: number;
  /** サーバーサイドページングの総件数。指定するとクライアント側で切り出さない */
  total?: number;
  onPageChange?: (page: number) => void;
}

export interface DataTableProps<T> {
  columns: ReadonlyArray<Column<T>>;
  rows: ReadonlyArray<T>;
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  /** 行選択チェックボックスを表示 */
  selectable?: boolean;
  selectedKeys?: ReadonlyArray<string | number>;
  onSelectionChange?: (keys: Array<string | number>) => void;
  /** ソート（制御）。onSortChange を渡すとサーバーソート扱い（rows を並べ替えない） */
  sort?: SortState | null;
  onSortChange?: (sort: SortState | null) => void;
  defaultSort?: SortState;
  pagination?: PaginationConfig;
  loading?: boolean;
  empty?: ReactNode;
  caption?: string;
  dense?: boolean;
  rowClassName?: (row: T) => string | undefined;
  /** 表の上部右側に表示（件数の横） */
  toolbar?: ReactNode;
  /** 行の展開表示：expandedKeys に含まれる行の直下に renderExpanded の内容を全列幅で表示 */
  expandedKeys?: ReadonlyArray<string | number>;
  renderExpanded?: (row: T) => ReactNode;
}

function readValue<T>(col: Column<T>, row: T): unknown {
  if (col.accessor) return col.accessor(row);
  return (row as Record<string, unknown>)[col.key];
}

function compare(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'ja', { numeric: true });
}

/**
 * 一覧表。ソート・ページング・行クリック・選択チェックに対応。
 * クライアント処理が既定。サーバー処理にする場合は onSortChange / pagination.total を渡す。
 */
export function DataTable<T>(props: DataTableProps<T>) {
  const {
    columns,
    rows,
    rowKey,
    onRowClick,
    selectable,
    selectedKeys = [],
    onSelectionChange,
    pagination,
    loading,
    empty,
    caption,
    dense,
    rowClassName,
    toolbar,
    expandedKeys,
    renderExpanded,
  } = props;

  const [innerSort, setInnerSort] = useState<SortState | null>(props.defaultSort ?? null);
  const [innerPage, setInnerPage] = useState(1);
  const sort = props.sort !== undefined ? props.sort : innerSort;
  const serverSort = !!props.onSortChange;
  const serverPaging = pagination?.total != null;

  const sorted = useMemo(() => {
    if (!sort || serverSort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col) return rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => compare(readValue(col, a), readValue(col, b)) * dir);
  }, [rows, sort, serverSort, columns]);

  const total = serverPaging ? (pagination?.total ?? 0) : sorted.length;
  const pageSize = pagination?.pageSize ?? (total || 1);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const page = Math.min(pagination?.page ?? innerPage, pageCount);
  const visible = pagination && !serverPaging ? sorted.slice((page - 1) * pageSize, page * pageSize) : sorted;

  const setPage = (p: number) => {
    const next = Math.min(Math.max(1, p), pageCount);
    if (pagination?.onPageChange) pagination.onPageChange(next);
    if (pagination?.page === undefined) setInnerPage(next);
  };

  const toggleSort = (key: string) => {
    let next: SortState | null;
    if (!sort || sort.key !== key) next = { key, direction: 'asc' };
    else if (sort.direction === 'asc') next = { key, direction: 'desc' };
    else next = null;
    if (props.sort === undefined) setInnerSort(next);
    props.onSortChange?.(next);
    if (pagination?.page === undefined) setInnerPage(1);
  };

  const selected = new Set(selectedKeys);
  const visibleKeys = visible.map(rowKey);
  const allChecked = visibleKeys.length > 0 && visibleKeys.every((k) => selected.has(k));
  const someChecked = visibleKeys.some((k) => selected.has(k));

  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) visibleKeys.forEach((k) => next.delete(k));
    else visibleKeys.forEach((k) => next.add(k));
    onSelectionChange?.([...next]);
  };
  const toggleOne = (k: string | number) => {
    const next = new Set(selected);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    onSelectionChange?.([...next]);
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className={cx('bs-table-wrap', dense && 'is-dense')}>
      <div className="bs-table-meta">
        <span className="bs-table-count">
          <strong className="bs-num">{formatNumber(total)}</strong> 件
          {pagination && total > 0 && (
            <span className="bs-table-range">
              （{formatNumber(from)}〜{formatNumber(to)} 件目を表示）
            </span>
          )}
          {selectable && selected.size > 0 && <span className="bs-table-selected">{formatNumber(selected.size)} 件選択中</span>}
        </span>
        {toolbar && <div className="bs-table-toolbar">{toolbar}</div>}
      </div>
      <div className="bs-table-scroll">
        <table className="bs-table" style={{ minWidth: tableMinWidth(columns, selectable) }}>
          {caption && <caption className="bs-visually-hidden">{caption}</caption>}
          <colgroup>
            {selectable && <col style={{ width: 44 }} />}
            {columns.map((c) => (
              <col key={c.key} style={c.width != null ? { width: c.width } : undefined} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selectable && (
                <th className="bs-table__check">
                  <Checkbox
                    aria-label="表示中の行をすべて選択"
                    checked={allChecked}
                    indeterminate={!allChecked && someChecked}
                    onChange={toggleAll}
                  />
                </th>
              )}
              {columns.map((c) => {
                const active = sort?.key === c.key;
                const align = c.align ?? (c.numeric ? 'right' : 'left');
                return (
                  <th
                    key={c.key}
                    scope="col"
                    className={cx(`is-${align}`, c.sortable && 'is-sortable', active && 'is-sorted')}
                    aria-sort={active ? (sort?.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {c.sortable ? (
                      <button type="button" className="bs-table__sort" onClick={() => toggleSort(c.key)}>
                        <span>{c.header}</span>
                        {active ? (
                          sort?.direction === 'asc' ? <ArrowUp size={13} aria-hidden /> : <ArrowDown size={13} aria-hidden />
                        ) : (
                          <ArrowUpDown size={13} aria-hidden className="bs-table__sort-idle" />
                        )}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => {
              const k = rowKey(row);
              const isSel = selected.has(k);
              const isExpanded = !!renderExpanded && !!expandedKeys?.includes(k);
              return (
                <Fragment key={k}>
                <tr
                  className={cx(onRowClick && 'is-clickable', isSel && 'is-selected', isExpanded && 'is-expanded', rowClassName?.(row))}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === 'Enter' && e.target === e.currentTarget) onRowClick(row);
                        }
                      : undefined
                  }
                  tabIndex={onRowClick ? 0 : undefined}
                >
                  {selectable && (
                    <td className="bs-table__check" onClick={(e) => e.stopPropagation()}>
                      <Checkbox aria-label="この行を選択" checked={isSel} onChange={() => toggleOne(k)} />
                    </td>
                  )}
                  {columns.map((c) => {
                    const align = c.align ?? (c.numeric ? 'right' : 'left');
                    const content = c.render
                      ? c.render(row, i)
                      : c.numeric
                        ? formatNumber(readValue(c, row) as number | null)
                        : (readValue(c, row) as ReactNode);
                    return (
                      <td key={c.key} className={cx(`is-${align}`, c.numeric && 'bs-num', c.nowrap && 'is-nowrap')}>
                        {content}
                      </td>
                    );
                  })}
                </tr>
                {isExpanded && (
                  <tr className="bs-table__expanded">
                    <td colSpan={columns.length + (selectable ? 1 : 0)}>
                      <div className="bs-table__expanded-body">{renderExpanded(row)}</div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {loading && (
          <div className="bs-table-loading">
            <Loading label="読み込み中" />
          </div>
        )}
        {!loading && visible.length === 0 && <div className="bs-table-empty">{empty ?? '該当するデータはありません。'}</div>}
      </div>
      {pagination && pageCount > 1 && <Pagination page={page} pageCount={pageCount} onChange={setPage} />}
    </div>
  );
}

export interface PaginationProps {
  page: number;
  pageCount: number;
  onChange: (page: number) => void;
}

function pageWindow(page: number, count: number): Array<number | '…'> {
  const out: Array<number | '…'> = [];
  const add = (n: number | '…') => out.push(n);
  const start = Math.max(2, page - 2);
  const end = Math.min(count - 1, page + 2);
  add(1);
  if (start > 2) add('…');
  for (let i = start; i <= end; i++) add(i);
  if (end < count - 1) add('…');
  if (count > 1) add(count);
  return out;
}

export function Pagination({ page, pageCount, onChange }: PaginationProps) {
  return (
    <nav className="bs-pager" aria-label="ページ送り">
      <button type="button" className="bs-pager__btn" onClick={() => onChange(1)} disabled={page <= 1} aria-label="最初のページ">
        <ChevronsLeft size={15} />
      </button>
      <button type="button" className="bs-pager__btn" onClick={() => onChange(page - 1)} disabled={page <= 1} aria-label="前のページ">
        <ChevronLeft size={15} />
      </button>
      {pageWindow(page, pageCount).map((p, i) =>
        p === '…' ? (
          <span key={`e${i}`} className="bs-pager__ellipsis">
            …
          </span>
        ) : (
          <button
            key={p}
            type="button"
            className={cx('bs-pager__btn bs-num', p === page && 'is-current')}
            aria-current={p === page ? 'page' : undefined}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        ),
      )}
      <button type="button" className="bs-pager__btn" onClick={() => onChange(page + 1)} disabled={page >= pageCount} aria-label="次のページ">
        <ChevronRight size={15} />
      </button>
      <button type="button" className="bs-pager__btn" onClick={() => onChange(pageCount)} disabled={page >= pageCount} aria-label="最後のページ">
        <ChevronsRight size={15} />
      </button>
    </nav>
  );
}
